/**
 * LookService — persistance DB des looks créés par l'utilisateur.
 *
 * v1.12.286 — ajout backend (POST/GET/DELETE /looks).
 *
 * Stratégie :
 *  - Source de vérité = DB (cross-device)
 *  - Cache AsyncStorage en local pour affichage instantané + offline
 *  - saveLook : POST → puis met à jour le cache
 *  - listLooks : tente GET, fallback cache si offline
 *  - deleteLook : DELETE + retire du cache
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { AWS_CONFIG } from '../config/aws_config';

const API_BASE = AWS_CONFIG.apiUrl;
const cacheKey = (uid) => `outfit_created_looks_${uid || 'guest'}`;
const etagKey  = (uid) => `outfit_created_looks_etag_${uid || 'guest'}`;

async function readCache(uid) {
  try {
    const raw = await AsyncStorage.getItem(cacheKey(uid));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

async function writeCache(uid, list) {
  try { await AsyncStorage.setItem(cacheKey(uid), JSON.stringify(list || [])); } catch {}
}

async function readEtag(uid) {
  try { return await AsyncStorage.getItem(etagKey(uid)); } catch { return null; }
}

async function writeEtag(uid, etag) {
  try {
    if (etag) await AsyncStorage.setItem(etagKey(uid), etag);
    else await AsyncStorage.removeItem(etagKey(uid));
  } catch {}
}

/**
 * Sauvegarde un look en DB + cache. Retourne true en cas de succès.
 */
export async function saveLook(look, { user, getValidToken }) {
  const uid = user?.id || 'guest';

  // Toujours mettre à jour le cache immédiatement (instant UI)
  const cache = await readCache(uid);
  const next = [look, ...cache.filter(l => l.id !== look.id)];
  await writeCache(uid, next);

  if (!getValidToken) return true; // pas d'auth disponible → save local only

  try {
    const token = await getValidToken();
    if (!token) return true;
    const res = await fetch(`${API_BASE}/looks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        id: look.id,
        name: look.name,
        occasion: look.occasion || null,
        items: look.items || [],
        suggestions: look.suggestions || [],
        score: typeof look.score === 'number' ? look.score : null,
        grade: look.grade || null,
        breakdown: look.breakdown || null,
      }),
    });
    if (!res.ok) {
      console.warn('[LookService] saveLook backend failed:', res.status);
      return true; // cache OK, on continue
    }
    return true;
  } catch (e) {
    console.warn('[LookService] saveLook network error:', e?.message);
    return true; // cache OK
  }
}

/**
 * Liste les looks. Tente la DB d'abord, fallback cache si offline / erreur.
 * Met à jour le cache avec le résultat DB pour le prochain offline.
 * v1.12.567 — ETag + gzip : 304 si rien changé (50ms au lieu de 300ms)
 */
export async function listLooks({ user, getValidToken }) {
  const uid = user?.id || 'guest';

  if (!getValidToken) return readCache(uid);

  try {
    const token = await getValidToken();
    if (!token) return readCache(uid);
    const cachedEtag = await readEtag(uid);
    const cache = await readCache(uid);
    // v1.12.916 — Si etag présent mais cache vide → desync (cache effacé,
    // etag persistant). On force un fresh fetch sans If-None-Match pour
    // récupérer les données serveur réelles.
    const safeSendEtag = cachedEtag && Array.isArray(cache) && cache.length > 0;
    const headers = { 'Authorization': `Bearer ${token}`, 'Accept-Encoding': 'gzip' };
    if (safeSendEtag) headers['If-None-Match'] = cachedEtag;
    const res = await fetch(`${API_BASE}/looks`, { headers });
    if (res.status === 304) {
      console.log(`[LookService] ⚡ 304 Not Modified — cache valide (${cache.length} looks)`);
      return cache;
    }
    if (!res.ok) {
      console.warn('[LookService] listLooks backend failed:', res.status);
      return cache;
    }
    const data = await res.json();
    if (!Array.isArray(data)) return cache;
    console.log(`[LookService] fresh fetch: ${data.length} looks`);
    // Refresh cache + nouvel ETag
    await writeCache(uid, data);
    const newEtag = res.headers.get('ETag');
    if (newEtag) await writeEtag(uid, newEtag);
    return data;
  } catch (e) {
    console.warn('[LookService] listLooks network error:', e?.message);
    return readCache(uid);
  }
}

/**
 * Supprime un look (DB + cache).
 */
export async function deleteLook(lookId, { user, getValidToken }) {
  const uid = user?.id || 'guest';

  // Retire du cache immédiatement
  const cache = await readCache(uid);
  await writeCache(uid, cache.filter(l => l.id !== lookId));

  if (!getValidToken) return true;
  try {
    const token = await getValidToken();
    if (!token) return true;
    await fetch(`${API_BASE}/looks/${encodeURIComponent(lookId)}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return true;
  } catch (e) {
    console.warn('[LookService] deleteLook network error:', e?.message);
    return true;
  }
}
